import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { api } from './api'

/**
 * A price the platform is asking the owner to agree to.
 *
 * The server names the offer — "discards", "credits", "room" — and the portal words it, in the
 * language it is being read in. Shared by the conversation and the queue: both refuse for the same
 * reasons, and a refusal that offers a way on in one place and a full stop in the other is two
 * products in one screen.
 *
 * Saying no is a button too, where saying no means something. An offer with only one answer is
 * not an offer.
 */
/**
 * What was answered to a question or an offer that is no longer on the table, marked — when what
 * the owner said next is one of the answers that were offered. Anything else they said was about
 * something else, and is not painted as a choice that never happened. Yesterday's buttons stay in
 * the transcript as a record, not as a control: a chip that could still be tapped a week later
 * was a second campaign nobody asked for.
 */
export function Chosen({ answer, among }) {
  if (!answer) return null

  const said = answer.trim().toLowerCase()
  const chosen = (among ?? []).find((option) => option && option.trim().toLowerCase() === said)

  return chosen ? (
    <div className="question__options">
      <span className="chip chip--chosen">{chosen}</span>
    </div>
  ) : null
}

export function DiscardsOffer({ busy, settled, chosen, onAccept, onDecline }) {
  const { t } = useTranslation()

  if (settled) {
    return (
      <>
        {t('campaign.discardsOffer')}
        <Chosen answer={chosen} among={[t('campaign.discardsAccept'), t('common.no')]} />
      </>
    )
  }

  return (
    <>
      {t('campaign.discardsOffer')}
      <div className="question__options">
        <button type="button" className="chip chip--pay" disabled={busy} onClick={onAccept}>
          {t('campaign.discardsAccept')}
        </button>
        {onDecline && (
          <button type="button" className="chip" disabled={busy} onClick={onDecline}>
            {t('common.no')}
          </button>
        )}
      </div>
    </>
  )
}

/**
 * A story drawn for a post that has no photographs to cut a video from.
 *
 * The conversation said why there is no video; this is the way on. The choices and their prices
 * come from the server — the price is a rule there, not a number typed here — and tapping one
 * starts the drawing at once. No model is asked what the tap meant.
 */
/**
 * Which layout, chosen by looking.
 *
 * Three pictures of the shop's own product, side by side, and the plain look beside them. The
 * names alone were an unfair question: "Акција", "Салон" and "Каталог" describe three things
 * nobody has seen, and picking by the sound of a word is guessing rather than choosing.
 *
 * Nothing here costs anything — the product's own photograph, dressed three times — so the offer
 * is a look at the work rather than a price to agree to.
 */
export function LayoutOffer({ busy, settled, chosen, layouts, onPick }) {
  const { t } = useTranslation()

  // A layout's row carries its name; a plain look is code, and is named here in the owner's
  // language. When the looks are among the pictures there is nothing for a "plain look" button
  // to add — every plain look is already on the table, drawn.
  const nameOf = (layout) => layout.name ?? t(`campaign.looks.${layout.key}`, { defaultValue: layout.key })
  const looksDrawn = (layouts ?? []).some((layout) => layout.kind === 'look')

  if (settled) {
    return (
      <>
        {t('campaign.layoutOffer')}
        <Chosen answer={chosen} among={[...(layouts ?? []).map(nameOf), t('campaign.layoutPlain')]} />
      </>
    )
  }

  return (
    <>
      {t('campaign.layoutOffer')}

      <div className="layouts">
        {(layouts ?? []).map((layout) => (
          <button
            key={layout.key}
            type="button"
            className="layout"
            disabled={busy}
            onClick={() => onPick(layout.key, nameOf(layout))}
          >
            <img className="layout__image" src={layout.image} alt="" loading="lazy" />
            <span className="layout__name">{nameOf(layout)}</span>
          </button>
        ))}
      </div>

      {!looksDrawn && (
        <div className="question__options">
          <button type="button" className="chip" disabled={busy} onClick={() => onPick(null, t('campaign.layoutPlain'))}>
            {t('campaign.layoutPlain')}
          </button>
        </div>
      )}
    </>
  )
}

export function StoryboardOffer({ busy, settled, chosen, post, choices, onPick, onDecline }) {
  const { t } = useTranslation()

  if (settled) {
    return (
      <>
        {t('campaign.storyboardOffer', { name: post?.name ?? '' })}
        <Chosen
          answer={chosen}
          among={[
            ...(choices ?? []).map((choice) => t('campaign.storyboardPick', { count: choice.scenes, credits: choice.credits })),
            t('common.no'),
          ]}
        />
      </>
    )
  }

  return (
    <>
      {t('campaign.storyboardOffer', { name: post?.name ?? '' })}
      <div className="question__options">
        {(choices ?? []).map((choice) => (
          <button
            key={choice.scenes}
            type="button"
            className="chip chip--pay"
            disabled={busy || !post}
            onClick={() => onPick(choice)}
          >
            {t('campaign.storyboardPick', { count: choice.scenes, credits: choice.credits })}
          </button>
        ))}
        {onDecline && (
          <button type="button" className="chip" disabled={busy} onClick={onDecline}>
            {t('common.no')}
          </button>
        )}
      </div>
      <div className="bubble__note">{t('campaign.storyboardPrice')}</div>
    </>
  )
}

/**
 * What to do when the credits are gone, or when every one of them is already held by a post that
 * is waiting.
 *
 * The packages, with what each holds and what it costs, and pay-as-you-go beside them. Payment
 * itself is not built — cPay is deliberately last — so tapping one records that they want it and
 * says so plainly rather than pretending a card was charged. Half a checkout would be worse than
 * none: the one thing a person must never be unsure about is whether they have just paid.
 */
export function CreditsOffer({ busy, settled, onAsked, lead, ways }) {
  const { t } = useTranslation()
  const [plans, setPlans] = useState(null)
  const [asked, setAsked] = useState(null)

  useEffect(() => {
    // The price list is fetched only while the offer is on the table.
    if (settled) return
    api.plans().then(setPlans).catch(() => setPlans([]))
  }, [settled])

  if (asked) return <>{t('campaign.creditsAsked', { plan: asked })}</>

  if (settled) return <>{lead ?? t('campaign.creditsOffer')}</>

  return (
    <>
      {lead ?? t('campaign.creditsOffer')}

      {/* Two of the three ways out cost nothing, so they are said before the price list. */}
      {ways && <div className="bubble__note">{t('campaign.roomWays')}</div>}

      <div className="plans">
        {(plans ?? []).map((plan) => (
          <button
            key={plan.code}
            type="button"
            className="planchip"
            disabled={busy}
            onClick={() => {
              const credits = plan.includedCredits > 0 ? plan.includedCredits : 10

              api
                .wantPlan(plan.code, credits)
                .then(() => {
                  setAsked(plan.name)
                  onAsked?.()
                })
                .catch(() => setAsked(plan.name))
            }}
          >
            <span className="planchip__name">{plan.name}</span>
            <span className="planchip__price">{Math.round(plan.priceMkd)} ден</span>
            <span className="planchip__credits">
              {plan.includedCredits > 0
                ? t('campaign.planCredits', { count: plan.includedCredits })
                : t('campaign.planPerPost')}
            </span>
          </button>
        ))}
      </div>
    </>
  )
}
